import { useState } from 'react';
import { Loader2, Play, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

export function BROSRemovedPreviewPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosPreviewRemovedFromSource();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const inv = result?.invoices;
  const bk = result?.bookings;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Removed from BR OS Preview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Dry-run preview: compares all records in bros_invoices and bros_bookings
          against the live BR OS API. Shows which records would be marked
          removed_from_source on the next sync. Does not write anything.
        </p>

        <Button onClick={run} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run preview
        </Button>

        {result?.error && (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" />
            {result.error}
          </div>
        )}

        {result && !result.error && (
          <div className="space-y-4">
            {/* Invoices summary */}
            {inv && (
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium">Invoices</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  <span className="text-muted-foreground">In DB (active)</span>
                  <span className="tabular-nums">{fmtInt(inv.count_in_db)}</span>
                  <span className="text-muted-foreground">In BR OS API</span>
                  <span className="tabular-nums">{fmtInt(inv.count_in_api)}</span>
                  <span className="text-muted-foreground">To be marked removed</span>
                  <span className="tabular-nums font-semibold">
                    {inv.count_to_be_marked_removed > 0 ? (
                      <Badge variant="destructive">{fmtInt(inv.count_to_be_marked_removed)}</Badge>
                    ) : (
                      fmtInt(inv.count_to_be_marked_removed)
                    )}
                  </span>
                  <span className="text-muted-foreground">Already removed</span>
                  <span className="tabular-nums">{fmtInt(inv.already_removed)}</span>
                  <span className="text-muted-foreground">Outstanding to remove</span>
                  <span className="tabular-nums font-semibold">{fmt(inv.sum_outstanding_to_be_removed)}</span>
                  <span className="text-muted-foreground">Total amount to remove</span>
                  <span className="tabular-nums">{fmt(inv.sum_total_to_be_removed)}</span>
                </div>

                {inv.sample && inv.sample.length > 0 && (
                  <details className="text-xs mt-2">
                    <summary className="cursor-pointer text-sm text-muted-foreground font-medium">
                      Sample invoices ({inv.sample.length})
                    </summary>
                    <div className="mt-2 overflow-x-auto border rounded-lg">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1.5 font-medium">ID</th>
                            <th className="text-left px-2 py-1.5 font-medium">Company</th>
                            <th className="text-left px-2 py-1.5 font-medium">Status</th>
                            <th className="text-right px-2 py-1.5 font-medium">Total</th>
                            <th className="text-right px-2 py-1.5 font-medium">Outstanding</th>
                            <th className="text-left px-2 py-1.5 font-medium">Currency</th>
                            <th className="text-left px-2 py-1.5 font-medium">Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inv.sample.map((row) => (
                            <tr key={row.bros_id} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono">{row.bros_id}</td>
                              <td className="px-2 py-1">{row.company || '-'}</td>
                              <td className="px-2 py-1">
                                <Badge variant="outline">{row.status || '-'}</Badge>
                              </td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmt(row.total_amount)}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmt(row.amount_outstanding)}</td>
                              <td className="px-2 py-1">{row.currency}</td>
                              <td className="px-2 py-1">{row.due_date || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            )}

            {/* Bookings summary */}
            {bk && (
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium">Bookings</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  <span className="text-muted-foreground">In DB (active)</span>
                  <span className="tabular-nums">{fmtInt(bk.count_in_db)}</span>
                  <span className="text-muted-foreground">In BR OS API</span>
                  <span className="tabular-nums">{fmtInt(bk.count_in_api)}</span>
                  <span className="text-muted-foreground">To be marked removed</span>
                  <span className="tabular-nums font-semibold">
                    {bk.count_to_be_marked_removed > 0 ? (
                      <Badge variant="destructive">{fmtInt(bk.count_to_be_marked_removed)}</Badge>
                    ) : (
                      fmtInt(bk.count_to_be_marked_removed)
                    )}
                  </span>
                  <span className="text-muted-foreground">Already removed</span>
                  <span className="tabular-nums">{fmtInt(bk.already_removed)}</span>
                  <span className="text-muted-foreground">Client total to remove</span>
                  <span className="tabular-nums font-semibold">{fmt(bk.sum_client_total_to_be_removed)}</span>
                </div>

                {bk.sample && bk.sample.length > 0 && (
                  <details className="text-xs mt-2">
                    <summary className="cursor-pointer text-sm text-muted-foreground font-medium">
                      Sample bookings ({bk.sample.length})
                    </summary>
                    <div className="mt-2 overflow-x-auto border rounded-lg">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1.5 font-medium">ID</th>
                            <th className="text-left px-2 py-1.5 font-medium">Company</th>
                            <th className="text-left px-2 py-1.5 font-medium">Status</th>
                            <th className="text-right px-2 py-1.5 font-medium">Client Total</th>
                            <th className="text-left px-2 py-1.5 font-medium">Currency</th>
                            <th className="text-left px-2 py-1.5 font-medium">Check-in</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bk.sample.map((row) => (
                            <tr key={row.bros_id} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono">{row.bros_id}</td>
                              <td className="px-2 py-1">{row.company || '-'}</td>
                              <td className="px-2 py-1">
                                <Badge variant="outline">{row.status || '-'}</Badge>
                              </td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmt(row.client_total)}</td>
                              <td className="px-2 py-1">{row.currency}</td>
                              <td className="px-2 py-1">{row.check_in || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
